/**
 * FullscreenButton — a self-contained fullscreen toggle for the POS surfaces.
 * Takes the whole page fullscreen (the sell screen and the display each fill
 * 100dvh already), reflecting the live state with an enter/exit icon. Hidden
 * on browsers without the Fullscreen API. A plain functional scaffold — the
 * design pass restyles it.
 */
import { FullscreenExitOutlined, FullscreenOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";

import { useFullscreen } from "../../hooks/useFullscreen";

export interface FullscreenButtonProps {
  /** AntD button type — "text" for an unobtrusive corner control (default), or
   *  "default" for a bordered button that sits in a toolbar. */
  type?: "text" | "default";
  size?: "small" | "middle" | "large";
  className?: string;
}

export const FullscreenButton = ({
  type = "text",
  size = "middle",
  className,
}: FullscreenButtonProps) => {
  const { isFullscreen, supported, toggle } = useFullscreen();
  if (!supported) return null;
  return (
    <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
      <Button
        type={type}
        size={size}
        className={className}
        aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
        onClick={() => void toggle()}
      />
    </Tooltip>
  );
};
